/**
 * Minimal ANSI SGR (Select Graphic Rendition) parser.
 *
 * Converts raw terminal output (with ANSI escape sequences) into an array of
 * styled spans. Handles the subset of codes that PowerShell and CMD actually
 * emit: foreground/background colors (standard + bright), bold, underline,
 * inverse, and reset. Cursor-movement sequences (CSI A/B/C/D/H/J/K) are
 * stripped (not rendered) since we're a scrolling log, not a full VT100 grid.
 */

export interface StyledSpan {
  text: string;
  fg: string | null;
  bg: string | null;
  bold: boolean;
  underline: boolean;
  inverse: boolean;
}

/** Standard 3/4-bit ANSI color palette */
const COLORS = [
  "#0c0c0c", // 0 black
  "#c50f1f", // 1 red
  "#13a10e", // 2 green
  "#c19c00", // 3 yellow
  "#0037da", // 4 blue
  "#881798", // 5 magenta
  "#3a96dd", // 6 cyan
  "#cccccc", // 7 white
];

const BRIGHT_COLORS = [
  "#767676", // 8  bright black
  "#e74856", // 9  bright red
  "#16c60c", // 10 bright green
  "#f9f1a5", // 11 bright yellow
  "#3b78ff", // 12 bright blue
  "#b4009e", // 13 bright magenta
  "#61d6d6", // 14 bright cyan
  "#f2f2f2", // 15 bright white
];

interface SgrState {
  fg: string | null;
  bg: string | null;
  bold: boolean;
  underline: boolean;
  inverse: boolean;
}

function defaultState(): SgrState {
  return { fg: null, bg: null, bold: false, underline: false, inverse: false };
}

/**
 * Apply a single SGR parameter to the state.
 */
function applySgr(state: SgrState, code: number): void {
  if (code === 0) {
    Object.assign(state, defaultState());
  } else if (code === 1) {
    state.bold = true;
  } else if (code === 4) {
    state.underline = true;
  } else if (code === 7) {
    state.inverse = true;
  } else if (code === 22) {
    state.bold = false;
  } else if (code === 24) {
    state.underline = false;
  } else if (code === 27) {
    state.inverse = false;
  } else if (code >= 30 && code <= 37) {
    state.fg = COLORS[code - 30];
  } else if (code === 39) {
    state.fg = null;
  } else if (code >= 40 && code <= 47) {
    state.bg = COLORS[code - 40];
  } else if (code === 49) {
    state.bg = null;
  } else if (code >= 90 && code <= 97) {
    state.fg = BRIGHT_COLORS[code - 90];
  } else if (code >= 100 && code <= 107) {
    state.bg = BRIGHT_COLORS[code - 100];
  }
}

/**
 * Parse a string containing ANSI escape sequences into styled spans.
 * Non-SGR CSI sequences (cursor movement, erase) are stripped.
 */
export function parseAnsi(input: string): StyledSpan[] {
  const spans: StyledSpan[] = [];
  const state = defaultState();
  let buf = "";

  // Regex matches CSI sequences: ESC[ followed by params and a final letter
  // Also matches OSC sequences: ESC] ... BEL/ST (window title etc -- strip)
  const CSI = /\x1b\[([0-9;]*)([A-Za-z])/g;
  const OSC = /\x1b\][^\x07\x1b]*(?:\x07|\x1b\\)/g;

  // Strip OSC sequences first (window title, etc.)
  let cleaned = input.replace(OSC, "");

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  CSI.lastIndex = 0;
  while ((match = CSI.exec(cleaned)) !== null) {
    // Flush text before this sequence
    const before = cleaned.slice(lastIndex, match.index);
    if (before) {
      buf += before;
    }
    lastIndex = match.index + match[0].length;

    const params = match[1];
    const cmd = match[2];

    if (cmd === "m") {
      // SGR -- flush current buffer then apply
      if (buf) {
        spans.push({ text: buf, ...state });
        buf = "";
      }
      if (params === "" || params === "0") {
        Object.assign(state, defaultState());
      } else {
        for (const p of params.split(";")) {
          applySgr(state, parseInt(p, 10) || 0);
        }
      }
    }
    // All other CSI commands (A/B/C/D/H/J/K/etc.) are silently stripped
  }

  // Flush remaining text
  const tail = cleaned.slice(lastIndex);
  if (tail) {
    buf += tail;
  }
  if (buf) {
    spans.push({ text: buf, ...state });
  }

  return spans;
}
